import styled, { keyframes } from 'styled-components';

const glowPulse = keyframes`
  0% {
    box-shadow: 0 0 10px rgba(64, 128, 255, 0.2);
  }
  50% {
    box-shadow: 0 0 20px rgba(64, 128, 255, 0.4);
  }
  100% {
    box-shadow: 0 0 10px rgba(64, 128, 255, 0.2);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const reflectionEffect = keyframes`
  0% {
    left: -100%;
  }
  50% {
    left: 100%;
  }
  100% {
    left: 100%;
  }
`;

export const OnboardingContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 3rem 1.5rem;
  color: #fff;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  
  &::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
      radial-gradient(circle at 10% 10%, rgba(64, 128, 255, 0.1), transparent 40%),
      radial-gradient(circle at 90% 90%, rgba(0, 200, 255, 0.1), transparent 40%);
    z-index: -1;
    pointer-events: none;
  }
`;

export const OnboardingHeader = styled.header`
  text-align: center;
  margin-bottom: 3rem;
  animation: ${fadeIn} 0.8s ease-out;
`;

export const OnboardingTitle = styled.h1`
  font-family: 'Orbitron', sans-serif;
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
  background: linear-gradient(to right, #64B6FF, #00FFFF);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 0 10px rgba(64, 128, 255, 0.3);
`;

export const OnboardingSubtitle = styled.p`
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.8);
  max-width: 600px;
  margin: 0 auto;
`;

export const PlatformsContainer = styled.div`
  flex: 1;
  animation: ${fadeIn} 0.8s ease-out;
`;

export const PlatformCard = styled.div`
  background: rgba(15, 25, 45, 0.8);
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;
  border: 1px solid rgba(64, 128, 255, 0.2);
  animation: ${glowPulse} 4s infinite;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 5px;
    background: linear-gradient(to right, #64B6FF, #00BFFF);
  }
`;

export const PlatformHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
`;

export const PlatformIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: ${props => props.color || '#4C6EF5'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  margin-right: 1rem;
  box-shadow: 0 0 15px ${props => props.color || '#4C6EF5'}80;
`;

export const PlatformTitle = styled.h2`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.8rem;
  margin: 0;
`;

export const ProgressBar = styled.div`
  height: 8px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  margin-bottom: 2rem;
  overflow: hidden;
`;

export const ProgressFill = styled.div`
  height: 100%;
  width: ${props => props.width || 0}%;
  background-color: ${props => props.color || '#4C6EF5'};
  border-radius: 4px;
  transition: width 0.3s ease;
`;

export const PlatformSteps = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

export const StepItem = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  transition: all 0.3s ease;
  border-left: 3px solid ${props => props.completed ? '#00A67E' : 'rgba(64, 128, 255, 0.5)'};
  
  &:hover {
    background: rgba(0, 0, 0, 0.3);
  }
`;

export const StepNumber = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: ${props => props.completed ? props.color : 'rgba(255, 255, 255, 0.1)'};
  color: ${props => props.completed ? '#fff' : 'rgba(255, 255, 255, 0.8)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-right: 1rem;
  flex-shrink: 0;
  transition: all 0.3s ease;
`;

export const CompletedCheckmark = styled.div`
  color: #fff;
  font-size: 0.9rem;
`;

export const StepText = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

export const StepLink = styled.a`
  color: #64B6FF;
  text-decoration: none;
  margin-top: 0.5rem;
  display: inline-block;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    color: #00FFFF;
    text-decoration: underline;
  }
  
  &::after {
    content: ' →';
  }
`;

export const NavigationButtons = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 2rem;
`;

export const NavigationButton = styled.button`
  padding: 0.8rem 1.5rem;
  border-radius: 8px;
  font-family: 'Orbitron', sans-serif;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
  overflow: hidden;
  box-shadow: ${props => props.subtle ? 'none' : '0 4px 6px rgba(0, 0, 0, 0.1)'};
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: all 0.6s ease;
  }
  
  &:hover::before {
    left: 100%;
  }
  
  ${props => props.primary ? `
    background: linear-gradient(135deg, #0070f3, #00BFFF);
    color: white;
    border: none;
    
    &:hover {
      background: linear-gradient(135deg, #0051cc, #0099cc);
      transform: translateY(-2px);
      box-shadow: 0 6px 15px rgba(0, 112, 243, 0.5);
    }
    
    &:active {
      transform: translateY(1px);
      box-shadow: 0 2px 8px rgba(0, 112, 243, 0.4);
    }
  ` : props.subtle ? `
    background: transparent;
    color: rgba(255, 255, 255, 0.5);
    border: none;
    padding: 0.6rem 1rem;
    font-size: 0.9rem;
    font-weight: 400;
    letter-spacing: 0.3px;
    
    svg {
      font-size: 0.8rem;
      opacity: 0.7;
    }
    
    &:hover {
      color: rgba(255, 255, 255, 0.8);
      background: rgba(0, 0, 0, 0.1);
      transform: none;
    }
    
    &:active {
      transform: translateY(1px);
    }
  ` : props.outline ? `
    background: rgba(0, 0, 0, 0.2);
    color: ${props.color || '#64B6FF'};
    border: 2px solid ${props.color || '#64B6FF'};
    font-weight: 600;
    letter-spacing: 0.5px;
    backdrop-filter: blur(5px);
    
    &:hover {
      background: rgba(${props.color ? props.color.replace('#', '').match(/.{2}/g).map(hex => parseInt(hex, 16)).join(', ') : '100, 182, 255'}, 0.1);
      box-shadow: 0 0 15px rgba(${props.color ? props.color.replace('#', '').match(/.{2}/g).map(hex => parseInt(hex, 16)).join(', ') : '100, 182, 255'}, 0.3);
      transform: translateY(-2px);
    }
    
    &:active {
      transform: translateY(1px);
    }
  ` : `
    background: transparent;
    color: rgba(255, 255, 255, 0.8);
    border: 1px solid rgba(255, 255, 255, 0.2);
    
    &:hover {
      background: rgba(255, 255, 255, 0.05);
      border-color: rgba(255, 255, 255, 0.3);
      transform: translateY(-1px);
    }
    
    &:active {
      transform: translateY(1px);
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
    }
  `}
`;

export const WelcomeSection = styled.div`
  text-align: center;
  max-width: 700px;
  margin: 0 auto;
  animation: ${fadeIn} 0.8s ease-out;
`;

export const WelcomeMessage = styled.div`
  background: rgba(15, 25, 45, 0.8);
  border-radius: 12px;
  padding: 2rem;
  border: 1px solid rgba(64, 128, 255, 0.2);
  animation: ${glowPulse} 4s infinite;
  text-align: left;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(to right, #64B6FF, #00FFFF);
  }
  
  p {
    margin-bottom: 1.5rem;
    line-height: 1.7;
    color: rgba(255, 255, 255, 0.9);
    font-size: 1.05rem;
    text-shadow: 0 0 10px rgba(0, 0, 0, 0.5);
    position: relative;
    z-index: 2;
  }
  
  ul {
    margin: 2rem 0;
    padding-left: 0;
    
    li {
      margin-bottom: 1.2rem;
      list-style-type: none;
      display: flex;
      align-items: center;
      padding: 0.8rem 1rem;
      background: rgba(0, 0, 0, 0.2);
      border-radius: 8px;
      transition: all 0.3s ease;
      border-left: 3px solid transparent;
      
      &:hover {
        background: rgba(0, 0, 0, 0.3);
        transform: translateX(5px);
      }
      
      &:nth-child(1) {
        border-left-color: #F22F46;
      }
      
      &:nth-child(2) {
        border-left-color: #4C6EF5;
      }
      
      &:nth-child(3) {
        border-left-color: #00A67E;
      }
    }
  }
`;

export const WelcomeHighlight = styled.h1`
  color: #64B6FF;
  font-size: 2rem;
  margin-bottom: 1rem;
  text-align: center;
  font-family: 'Orbitron', sans-serif;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

export const ImportantNotice = styled.div`
  padding: 1.2rem;
  background: rgba(64, 128, 255, 0.15);
  border-radius: 8px;
  border-left: 4px solid #4C6EF5;
  margin-top: 1.5rem;
  
  .notice-header {
    display: flex;
    align-items: center;
    margin-bottom: 0.5rem;
  }
  
  strong {
    color: #64B6FF;
    font-weight: bold;
    margin-left: 0.5rem;
  }
  
  .highlight {
    color: #64B6FF;
    font-weight: bold;
  }
  
  p {
    margin: 0;
  }
`;

export const LogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin: 2rem 0;
  
  .platform-item {
    display: flex;
    align-items: center;
    background: rgba(0, 0, 0, 0.2);
    border-radius: 10px;
    padding: 1rem;
    position: relative;
    overflow: hidden;
  }
  
  .platform-item.twilio {
    border-left: 4px solid #F22F46;
  }
  
  .platform-item.vapi {
    border-left: 4px solid #00A67E;
  }
  
  .platform-item.openai {
    border-left: 4px solid #4C6EF5;
  }
  
  .platform-item.meta {
    border-left: 4px solid #1877F3;
  }
  
  .platform-logo {
    width: 48px;
    height: 48px;
    object-fit: contain;
    margin-right: 1rem;
  }
  
  .platform-content {
    display: flex;
    flex-direction: column;
  }
  
  .platform-name {
    font-weight: bold;
    font-size: 1.1rem;
    margin-bottom: 0.25rem;
  }
  
  p {
    font-size: 0.9rem;
    opacity: 0.8;
    margin: 0;
  }
`;

export const StartConfigButton = styled.button`
  background: #1e2b3a;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 0.8rem 1.5rem;
  border-radius: 8px;
  font-family: 'Orbitron', sans-serif;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    background: #2a3a4a;
    
    &::before {
      left: 100%;
    }
  }
  
  &:active {
    transform: translateY(1px);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: all 0.6s ease;
  }
  
  svg {
    margin-left: 0.5rem;
  }
`;

export default {
  OnboardingContainer,
  OnboardingHeader,
  OnboardingTitle,
  OnboardingSubtitle,
  PlatformsContainer,
  PlatformCard,
  PlatformHeader,
  PlatformIcon,
  PlatformTitle,
  PlatformSteps,
  StepItem,
  StepNumber,
  StepText,
  StepLink,
  CompletedCheckmark,
  ProgressBar,
  ProgressFill,
  NavigationButtons,
  NavigationButton,
  WelcomeSection,
  WelcomeMessage,
  WelcomeHighlight,
  ImportantNotice,
  LogoContainer,
  StartConfigButton
};
